import {WaveLoader} from '@wandb/weave/components/Loaders/WaveLoader';
import {Tag} from '@wandb/weave/components/Tag';
import {clsx} from 'clsx';
import React, {FC, useCallback, useMemo} from 'react';

import {PaginationControls} from '../common/PaginationControls';
import {StatusChip} from '../common/StatusChip';
import {extractChatMesage} from '../ThreadsPage/common';
import {ComputedCallStatusType} from '../wfReactInterface/traceServerClientTypes';
import {
  parseSpanName,
  traceCallStatusCode,
  useThreadTurns,
} from '../wfReactInterface/tsDataModelHooks';

export interface Turn {
  id: string;
  status: ComputedCallStatusType;
  input?: string;
  opName: string;
}

interface ThreadTurnItemProps {
  turn: Turn;
  index: number;
  isSelected: boolean;
  firstTurnIndex: number;
  onTurnClick: (index: number) => void;
}

const ThreadTurnItem: FC<ThreadTurnItemProps> = ({
  turn,
  index,
  isSelected,
  firstTurnIndex,
  onTurnClick,
}) => {
  return (
    <div
      key={turn.id}
      className={clsx(
        'group flex w-full cursor-pointer items-center overflow-hidden border-b border-moon-200 px-16 py-8 transition-colors',
        isSelected && 'bg-[rgba(169,237,242,0.32)]',
        !isSelected && 'hover:bg-moon-100'
      )}
      onClick={() => onTurnClick(index)}>
      <div className="w-full">
        {/* Row 1 */}
        <div className="mb-2 flex w-full items-center">
          <p className="mr-4 mr-auto line-clamp-1 truncate text-sm">
            {turn.opName}
          </p>
          <div className="hidden group-hover:flex">
            <Tag label={`${firstTurnIndex + index + 1}`} color="moon" />
          </div>
          <div className="flex group-hover:hidden">
            <StatusChip value={turn.status} iconOnly />
          </div>
        </div>
        {/* Row 2 */}
        {turn.input && (
          <div className="flex h-24 items-center justify-between truncate text-sm text-moon-500">
            <p className="mr-auto truncate">{turn.input}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export interface ThreadTurnsListProps {
  turnsState: ReturnType<typeof useThreadTurns>['turnsState'];
  selectedTurnId?: string;
  onTurnSelect?: (index: number) => void;
  totalTurns: number;
  currentPage: number;
  totalPages: number;
  turnPrevPage: () => void;
  turnNextPage: () => void;
  turnsLoading: boolean;
  turnsError: Error | undefined;
  firstTurnIndex: number;
}

export const ThreadTurnsList: FC<ThreadTurnsListProps> = ({
  turnsState,
  selectedTurnId,
  onTurnSelect,
  totalTurns,
  currentPage,
  totalPages,
  turnPrevPage,
  turnNextPage,
  turnsLoading,
  turnsError,
  firstTurnIndex,
}) => {
  const handleTurnClick = useCallback(
    (index: number) => {
      if (onTurnSelect) {
        onTurnSelect(index);
      }
    },
    [onTurnSelect]
  );

  const turns = useMemo(() => {
    if (turnsState.loading || !turnsState.value) {
      return [];
    }

    return turnsState.value.turnCalls.map((traceCall): Turn => {
      // Convert inputs object to a string preview
      const inputPreview = traceCall.inputs
        ? extractChatMesage(traceCall.inputs)
        : undefined;

      return {
        id: traceCall.id,
        status: traceCallStatusCode(traceCall),
        input: inputPreview,
        opName: parseSpanName(traceCall.op_name),
      };
    });
  }, [turnsState]);

  // Show a loading state if the turnsState is loading (initial load)
  if (turnsState.loading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <WaveLoader size="small" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="relative flex-1 overflow-y-auto">
        {/* Overlay loading indicator for pagination */}
        {turnsLoading && (
          <div className="flex w-full items-center justify-center">
            <WaveLoader size="small" />
          </div>
        )}

        {firstTurnIndex > 0 && (
          <div className="flex items-center justify-center text-sm text-moon-800">
            ... Turn 1 to {firstTurnIndex} are omitted ...
          </div>
        )}

        {/* Show error state for turns loading */}
        {turnsError && !turnsLoading && (
          <div className="flex items-center justify-center p-8 text-red-500">
            Error loading turns: {turnsError.message}
          </div>
        )}

        {/* Show turns list (preserved during loading) */}
        {!turnsError &&
          turns.map((turn, index) => {
            const isSelected = turn.id === selectedTurnId;

            return (
              <ThreadTurnItem
                key={turn.id}
                turn={turn}
                index={index}
                isSelected={isSelected}
                firstTurnIndex={firstTurnIndex}
                onTurnClick={handleTurnClick}
              />
            );
          })}
        {firstTurnIndex + turns.length < totalTurns && (
          <div className="flex items-center justify-center text-sm text-moon-800">
            ... Turn {firstTurnIndex + turns.length + 1} to {totalTurns} are
            omitted ...
          </div>
        )}
      </div>
      <div className="border-t border-moon-250 p-8">
        <PaginationControls
          currentPage={currentPage - 1} // PaginationControls uses 0-based indexing
          totalPages={totalPages}
          startIndex={firstTurnIndex + 1}
          endIndex={firstTurnIndex + turns.length}
          totalItems={totalTurns}
          onPrevPage={turnPrevPage}
          onNextPage={turnNextPage}
          className="h-[35px]"
        />
      </div>
    </div>
  );
};
